import { useEffect, useState } from "react";
import { NavLink, Route, Routes } from "react-router-dom";
import {
  Mic2,
  FileText,
  Calendar,
  Send,
  Inbox as InboxIcon,
  Settings as SettingsIcon,
  Sun,
  Moon,
  Menu,
  X,
  Command,
} from "lucide-react";
import Home from "./pages/Home";
import Drafts from "./pages/Drafts";
import Scheduled from "./pages/Scheduled";
import Published from "./pages/Published";
import Inbox from "./pages/Inbox";
import Settings from "./pages/Settings";
import { SettingsVoice } from "./pages/SettingsVoice";
import Login from "./pages/Login";
import AuthVerify from "./pages/AuthVerify";
import { AuthGate } from "./components/AuthGate";
import { Footer } from "./components/Footer";
import { LinkedInBanner } from "./components/LinkedInBanner";
import { Toaster } from "./components/ui/Toaster";
import { Badge } from "./components/ui/Badge";
import { CommandPalette } from "./components/ui/CommandPalette";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "./lib/api";
import { useDrafts } from "./hooks/useDrafts";
import { useInbox } from "./hooks/useInbox";
import { useTheme } from "./lib/theme";
import { Dialog as HDialog, Transition } from "@headlessui/react";
import { Fragment } from "react";

const App = () => {
  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/verify" element={<AuthVerify />} />
        <Route
          path="*"
          element={
            <AuthGate>
              <Layout />
            </AuthGate>
          }
        />
      </Routes>
      <Toaster />
    </>
  );
};

export default App;

interface NavEntry {
  to: string;
  label: string;
  icon: typeof FileText;
  badge?: number;
  highlightBadge?: boolean;
  prefetch?: () => void;
}

const Layout = () => {
  const qc = useQueryClient();
  const drafts = useDrafts();
  const inbox = useInbox();
  const inboxPending =
    inbox.data?.items.reduce((acc, g) => acc + g.engagements.length, 0) ?? 0;
  const draftsCount = drafts.data?.length ?? 0;

  const prefetch = (key: string, fn: () => Promise<unknown>) =>
    qc.prefetchQuery({ queryKey: [key], queryFn: fn, staleTime: 30_000 });

  const navItems: NavEntry[] = [
    {
      to: "/drafts",
      label: "Drafts",
      icon: FileText,
      badge: draftsCount > 0 ? draftsCount : undefined,
      prefetch: () => prefetch("drafts", () => api.drafts.list()),
    },
    {
      to: "/scheduled",
      label: "Agendados",
      icon: Calendar,
      prefetch: () => prefetch("scheduled", () => api.posts.scheduled()),
    },
    {
      to: "/published",
      label: "Publicados",
      icon: Send,
      prefetch: () => prefetch("published", () => api.posts.published()),
    },
    {
      to: "/inbox",
      label: "Inbox",
      icon: InboxIcon,
      badge: inboxPending > 0 ? inboxPending : undefined,
      highlightBadge: inboxPending > 0,
      prefetch: () => prefetch("inbox", () => api.inbox.list()),
    },
  ];

  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Atalho global ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 flex flex-col">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-accent focus:text-accent-fg focus:px-3 focus:py-1.5 focus:rounded-lg focus:text-sm focus:font-medium"
      >
        Pular para o conteúdo
      </a>
      <LinkedInBanner />
      <header className="sticky top-0 z-30 border-b border-zinc-200 dark:border-zinc-800 bg-white/85 dark:bg-zinc-900/85 backdrop-blur">
        <nav className="max-w-5xl mx-auto px-6 py-3 flex items-center gap-6">
          <NavLink to="/" className="flex items-center gap-2 font-semibold focus-ring rounded">
            <Mic2 className="text-accent" size={20} />
            <span>jp-voice</span>
          </NavLink>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((it) => (
              <NavItem key={it.to} {...it} />
            ))}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              aria-label="Abrir comandos (⌘K)"
              onClick={() => setPaletteOpen(true)}
              className="hidden sm:inline-flex items-center gap-2 h-9 px-2.5 rounded-lg text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800/50 focus-ring"
            >
              <Command size={14} />
              <kbd className="font-mono text-[10px] text-zinc-500 dark:text-zinc-400">⌘K</kbd>
            </button>
            <ThemeToggle />
            <NavIcon to="/settings" label="Settings" icon={SettingsIcon} />
            <button
              type="button"
              aria-label="Abrir menu"
              onClick={() => setMobileOpen(true)}
              className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 focus-ring"
            >
              <Menu size={18} />
            </button>
          </div>
        </nav>
      </header>

      <main id="main-content" tabIndex={-1} className="max-w-5xl mx-auto px-6 py-8 flex-1 w-full focus:outline-none">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/drafts" element={<Drafts />} />
          <Route path="/drafts/:id" element={<Drafts />} />
          <Route path="/scheduled" element={<Scheduled />} />
          <Route path="/published" element={<Published />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/voice" element={<SettingsVoice />} />
        </Routes>
      </main>

      <Footer />

      {/* Mobile drawer */}
      <MobileMenu
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        items={navItems}
      />

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
};

const NavItem = ({ to, label, icon: Icon, badge, highlightBadge, prefetch }: NavEntry) => {
  return (
    <NavLink
      to={to}
      onMouseEnter={() => prefetch?.()}
      onFocus={() => prefetch?.()}
      className={({ isActive }) =>
        `inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm transition-colors focus-ring ${
          isActive
            ? "bg-zinc-100 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100"
            : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800/50"
        }`
      }
    >
      <Icon size={15} />
      <span>{label}</span>
      {badge !== undefined && (
        <Badge variant={highlightBadge ? "accent" : "default"} size="sm">
          {badge}
        </Badge>
      )}
    </NavLink>
  );
};

const NavIcon = ({ to, label, icon: Icon }: { to: string; label: string; icon: typeof FileText }) => {
  return (
    <NavLink
      to={to}
      aria-label={label}
      className={({ isActive }) =>
        `inline-flex items-center justify-center h-9 w-9 rounded-lg transition-colors focus-ring ${
          isActive
            ? "bg-zinc-100 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100"
            : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800/50"
        }`
      }
    >
      <Icon size={16} />
    </NavLink>
  );
};

const ThemeToggle = () => {
  const { resolved, toggle } = useTheme();
  const Icon = resolved === "dark" ? Sun : Moon;
  return (
    <button
      type="button"
      aria-label={resolved === "dark" ? "Mudar para tema claro" : "Mudar para tema escuro"}
      onClick={toggle}
      className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800/50 focus-ring"
    >
      <Icon size={16} />
    </button>
  );
};

const MobileMenu = ({
  open,
  onClose,
  items,
}: {
  open: boolean;
  onClose: () => void;
  items: NavEntry[];
}) => {
  return (
    <Transition appear show={open} as={Fragment}>
      <HDialog as="div" className="relative z-50 md:hidden" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm" aria-hidden="true" />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="translate-x-full"
          enterTo="translate-x-0"
          leave="ease-in duration-150"
          leaveFrom="translate-x-0"
          leaveTo="translate-x-full"
        >
          <HDialog.Panel className="fixed inset-y-0 right-0 w-72 bg-white dark:bg-zinc-900 border-l border-zinc-200 dark:border-zinc-800 shadow-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold">Menu</span>
              <button
                type="button"
                aria-label="Fechar menu"
                onClick={onClose}
                className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 focus-ring"
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex flex-col gap-1">
              {items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-2 px-3 h-10 rounded-lg text-sm transition-colors focus-ring ${
                      isActive
                        ? "bg-zinc-100 dark:bg-zinc-800"
                        : "hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                    }`
                  }
                >
                  <it.icon size={16} />
                  <span className="flex-1">{it.label}</span>
                  {it.badge !== undefined && (
                    <Badge variant={it.highlightBadge ? "accent" : "default"} size="sm">
                      {it.badge}
                    </Badge>
                  )}
                </NavLink>
              ))}
              <NavLink
                to="/settings"
                onClick={onClose}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-3 h-10 rounded-lg text-sm transition-colors focus-ring ${
                    isActive ? "bg-zinc-100 dark:bg-zinc-800" : "hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                  }`
                }
              >
                <SettingsIcon size={16} />
                <span>Settings</span>
              </NavLink>
            </div>
          </HDialog.Panel>
        </Transition.Child>
      </HDialog>
    </Transition>
  );
};
